import {create} from 'zustand';
import {subscribeWithSelector} from 'zustand/middleware';

const VIEWER_TAB_DEFAULTS = {
    leftPanelTab: 'search',
    rightPanelTab: 'documentHighlights',
};

const VIEWER_CONTEXT_DEFAULT = 'standalone';

const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizeString = (value, fallback = null) => {
    if (value === null || value === undefined || value === '') return fallback;
    return String(value);
};

const updateUrlParams = (updates) => {
    if (typeof window === 'undefined') return;

    const url = new URL(window.location.href);

    Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, String(value));
        }
    });

    window.history.replaceState({}, '', url.toString());
};

const getStateFromSearchParams = (searchParams) => {
    const documentId = normalizeString(searchParams.get('documentId'));
    const segmentIndex = safeNumber(searchParams.get('segmentIndex'));
    const viewerMode = normalizeString(searchParams.get('viewerMode'), 'modal');
    const mode = viewerMode === 'page' ? 'page' : 'modal';

    const legacyModalOpen = searchParams.get('isDocumentViewerOpen') === 'true';

    return {
        // Backward compatibility: page mode can still be hydrated from legacy `viewerMode=page`.
        isOpen: mode === 'page' ? true : legacyModalOpen,
        mode,
        documentId,
        docType: normalizeString(searchParams.get('docType'), 'text'),
        viewerContext: normalizeString(searchParams.get('viewerContext'), VIEWER_CONTEXT_DEFAULT),
        reportId: normalizeString(searchParams.get('reportId')),
        leftPanelTab: normalizeString(searchParams.get('leftPanelTab'), VIEWER_TAB_DEFAULTS.leftPanelTab),
        rightPanelTab: normalizeString(searchParams.get('rightPanelTab'), VIEWER_TAB_DEFAULTS.rightPanelTab),
        selectedHighlightId: normalizeString(searchParams.get('highlightId')),
        searchQuery: normalizeString(searchParams.get('searchQuery')),
        referenceMarkId: normalizeString(searchParams.get('referenceMarkId')),
        sourceFileId: normalizeString(searchParams.get('sourceFileId')),
        sourceFolderId: normalizeString(searchParams.get('sourceFolderId')),
        segmentIndex,
    };
};

const useDocumentViewerStore = create(
    subscribeWithSelector((set, get) => ({
        isOpen: false,
        mode: 'modal',
        reportId: null,
        documentId: null,
        docType: 'text',
        viewerContext: VIEWER_CONTEXT_DEFAULT,
        leftPanelTab: VIEWER_TAB_DEFAULTS.leftPanelTab,
        rightPanelTab: VIEWER_TAB_DEFAULTS.rightPanelTab,
        selectedHighlightId: null,
        searchQuery: null,
        referenceMarkId: null,
        sourceFileId: null,
        sourceFolderId: null,
        segmentIndex: null,
        _initialized: false,

        openModal: ({
                        reportId = null,
                        documentId = null,
                        docType = 'text',
                        viewerContext = null,
                        segmentIndex = null,
                        searchQuery = null,
                        referenceMarkId = null,
                    } = {}) => {
            if (!documentId && !searchQuery && !referenceMarkId) {
                console.warn('[DocumentViewerStore] openModal() requires documentId or search context');
                return;
            }

            set({
                isOpen: true,
                mode: 'modal',
                reportId,
                documentId: normalizeString(documentId),
                docType,
                viewerContext: normalizeString(viewerContext, get().viewerContext || 'report'),
                segmentIndex: safeNumber(segmentIndex),
                searchQuery: normalizeString(searchQuery),
                referenceMarkId: normalizeString(referenceMarkId),
            });
        },

        openPage: ({
                       reportId = null,
                       documentId = null,
                       docType = 'text',
                       viewerContext = VIEWER_CONTEXT_DEFAULT,
                       segmentIndex = null,
                       searchQuery = null,
                       referenceMarkId = null,
                   } = {}) => {
            set({
                isOpen: true,
                mode: 'page',
                reportId,
                documentId: normalizeString(documentId),
                docType,
                viewerContext: normalizeString(viewerContext, VIEWER_CONTEXT_DEFAULT),
                segmentIndex: safeNumber(segmentIndex),
                searchQuery: normalizeString(searchQuery),
                referenceMarkId: normalizeString(referenceMarkId),
            });
        },

        close: () => {
            set({
                isOpen: false,
                mode: 'modal',
                reportId: null,
                documentId: null,
                docType: 'text',
                viewerContext: VIEWER_CONTEXT_DEFAULT,
                segmentIndex: null,
                selectedHighlightId: null,
                searchQuery: null,
                referenceMarkId: null,
                sourceFileId: null,
                sourceFolderId: null,
                rightPanelTab: VIEWER_TAB_DEFAULTS.rightPanelTab,
            });
        },

        setDocument: ({
                          reportId = null,
                          documentId,
                          docType = 'text',
                          segmentIndex = null,
                          viewerContext = null,
                          sourceFileId = null,
                          sourceFolderId = null,
                      } = {}) => {
            if (!documentId) return;

            set({
                reportId,
                documentId: normalizeString(documentId),
                docType,
                viewerContext: normalizeString(viewerContext, get().viewerContext),
                segmentIndex: safeNumber(segmentIndex),
                selectedHighlightId: null,
                searchQuery: null,
                referenceMarkId: null,
                sourceFileId: normalizeString(sourceFileId),
                sourceFolderId: normalizeString(sourceFolderId),
            });
        },

        setViewerContext: (viewerContext) => {
            set({viewerContext: normalizeString(viewerContext, VIEWER_CONTEXT_DEFAULT)});
        },

        setLeftPanelTab: (leftPanelTab) => {
            set({leftPanelTab: normalizeString(leftPanelTab, VIEWER_TAB_DEFAULTS.leftPanelTab)});
        },

        setRightPanelTab: (rightPanelTab) => {
            set({rightPanelTab: normalizeString(rightPanelTab, VIEWER_TAB_DEFAULTS.rightPanelTab)});
        },

        setSelection: ({selectedHighlightId = null, segmentIndex = null} = {}) => {
            set({
                selectedHighlightId: normalizeString(selectedHighlightId),
                segmentIndex: safeNumber(segmentIndex),
            });
        },

        initFromUrl: (searchParams) => {
            if (get()._initialized) return;

            const parsed = getStateFromSearchParams(searchParams);

            if (parsed.isOpen || parsed.mode === 'page') {
                set({...parsed, _initialized: true});
                return;
            }

            set({_initialized: true});
        },

        reset: () => {
            set({
                isOpen: false,
                mode: 'modal',
                reportId: null,
                documentId: null,
                docType: 'text',
                viewerContext: VIEWER_CONTEXT_DEFAULT,
                leftPanelTab: VIEWER_TAB_DEFAULTS.leftPanelTab,
                rightPanelTab: VIEWER_TAB_DEFAULTS.rightPanelTab,
                selectedHighlightId: null,
                searchQuery: null,
                referenceMarkId: null,
                sourceFileId: null,
                sourceFolderId: null,
                segmentIndex: null,
                _initialized: false,
            });
        },
    }))
);

useDocumentViewerStore.subscribe(
    (state) => ({
        isOpen: state.isOpen,
        mode: state.mode,
        reportId: state.reportId,
        documentId: state.documentId,
        docType: state.docType,
        viewerContext: state.viewerContext,
        leftPanelTab: state.leftPanelTab,
        rightPanelTab: state.rightPanelTab,
        selectedHighlightId: state.selectedHighlightId,
        searchQuery: state.searchQuery,
        referenceMarkId: state.referenceMarkId,
        sourceFileId: state.sourceFileId,
        sourceFolderId: state.sourceFolderId,
        segmentIndex: state.segmentIndex,
    }),
    (state) => {
        if (!useDocumentViewerStore.getState()._initialized) return;

        const shouldPersist = state.mode === 'page' || state.isOpen;

        if (shouldPersist) {
            updateUrlParams({
                // Legacy params are kept only for open modal share links.
                isDocumentViewerOpen: state.mode === 'modal' && state.isOpen ? 'true' : null,
                viewerMode: null,
                reportId: state.reportId,
                documentId: state.documentId,
                docType: state.docType,
                viewerContext: state.viewerContext,
                leftPanelTab: state.leftPanelTab,
                rightPanelTab: state.rightPanelTab,
                highlightId: state.selectedHighlightId,
                searchQuery: state.searchQuery,
                referenceMarkId: state.referenceMarkId,
                sourceFileId: state.sourceFileId,
                sourceFolderId: state.sourceFolderId,
                segmentIndex: state.segmentIndex,
            });
            return;
        }

        updateUrlParams({
            isDocumentViewerOpen: null,
            viewerMode: null,
            reportId: null,
            documentId: null,
            docType: null,
            viewerContext: null,
            leftPanelTab: null,
            rightPanelTab: null,
            highlightId: null,
            searchQuery: null,
            referenceMarkId: null,
            sourceFileId: null,
            sourceFolderId: null,
            segmentIndex: null,
        });
    },
    {
        equalityFn: (a, b) => (
            a.isOpen === b.isOpen
            && a.mode === b.mode
            && a.reportId === b.reportId
            && a.documentId === b.documentId
            && a.docType === b.docType
            && a.viewerContext === b.viewerContext
            && a.leftPanelTab === b.leftPanelTab
            && a.rightPanelTab === b.rightPanelTab
            && a.selectedHighlightId === b.selectedHighlightId
            && a.searchQuery === b.searchQuery
            && a.referenceMarkId === b.referenceMarkId
            && a.sourceFileId === b.sourceFileId
            && a.sourceFolderId === b.sourceFolderId
            && a.segmentIndex === b.segmentIndex
        )
    }
);

export default useDocumentViewerStore;



